import { useState } from 'react';
import { ImageIcon, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '@/components/modal/Modal.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import PrimitiveFileInput from '@/components/form/_primitives/PrimitiveFileInput.tsx';
import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';
import {
  TemplateImageDto,
  TEMPLATE_IMAGE_ACCEPT,
  TEMPLATE_IMAGE_MAX_SIZE_BYTES,
} from '@/features/dashboard/templateImages/types/TemplateImageDto.ts';
import { TemplateImageSupport } from './templateImageSupport.ts';

interface TemplateImagePickerProps {
  imageSupport: TemplateImageSupport;
  onInsert: (image: TemplateImageDto) => void;
  onClose: () => void;
}

const TemplateImagePicker = ({
  imageSupport,
  onInsert,
  onClose,
}: TemplateImagePickerProps) => {
  const { showDialog } = useConfirmationDialogContext();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (file.size > TEMPLATE_IMAGE_MAX_SIZE_BYTES) {
      toast.error('Plik jest za duży', {
        description: 'Maksymalny rozmiar grafiki to 5 MB.',
      });
      return;
    }
    await imageSupport.upload(file);
  };

  const handleDelete = async (image: TemplateImageDto) => {
    const confirmed = await showDialog({
      title: 'Usuń grafikę',
      description: `Czy na pewno chcesz usunąć „${image.fileName}"? Szablony, które jej używają, przestaną ją wyświetlać.`,
      variant: 'destructive',
      confirmText: 'Usuń',
    });
    if (!confirmed) return;

    setDeletingId(image.id);
    try {
      await imageSupport.remove(image.id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal onClose={onClose} className="max-w-[640px]">
      <div className="sticky top-0 z-10 flex w-full items-center justify-between bg-basic-white px-4 py-3">
        <h2 className="text-xl font-semibold text-base-text">Grafiki</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-neutral-50"
        >
          <X className="text-neutral-900" size={20} />
        </button>
      </div>

      <ModalContent>
        <PrimitiveFileInput
          onDrop={handleDrop}
          accept={TEMPLATE_IMAGE_ACCEPT}
          isLoading={imageSupport.isUploading}
          hint="PNG, JPG, GIF lub WEBP • do 5 MB"
        />

        {imageSupport.images.length === 0 ? (
          <NoItems
            icon={<ImageIcon />}
            title="Brak grafik"
            description="Wgraj pierwszą grafikę, aby wstawić ją do szablonu."
          />
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {imageSupport.images.map((image) => (
              <div
                key={image.id}
                className="group relative flex flex-col gap-1"
              >
                <button
                  type="button"
                  onClick={() => onInsert(image)}
                  title={`Wstaw „${image.fileName}"`}
                  className="overflow-hidden rounded-md border border-neutral-200 transition hover:border-accent-600 focus:border-accent-600 focus:outline-none"
                >
                  <img
                    src={image.downloadUrl}
                    alt={image.fileName}
                    className="h-24 w-full bg-neutral-50 object-contain"
                  />
                </button>
                <ActionButton
                  iconOnly
                  tone="danger"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(image);
                  }}
                  isLoading={deletingId === image.id}
                  className="absolute right-1 top-1 opacity-0 transition group-hover:opacity-100"
                >
                  <Trash2 />
                </ActionButton>
                <span className="truncate text-xs text-neutral-500">
                  {image.fileName}
                </span>
              </div>
            ))}
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TemplateImagePicker;
